/**
 * The link probe: do two browsers on two networks find each other, and at what cost?
 *
 * A PAGE OF ITS OWN AND NOT A MODE OF THE CLIENT, which the tanda's scope asks for
 * (`hosted-tandas.md §5 · T1`). Both reasons are measurement discipline: a failure here has one possible
 * cause instead of two, and a phone on mobile data reaches the answer in a second rather than after the
 * ~82 MiB a field costs (`hosted.md §5.3`). What it produces is posted to the backend, because a
 * fraction is a question about a population and a number printed in one browser is not one.
 */
import { Api, ApiError, DEFAULT_API_BASE, iceProvider, type RoomGrant } from '../shared/coord/api.ts';
import { bearer, forgetBearer, rememberBearer, signOut } from '../shared/coord/bearer.ts';
import { claimTicket, DoorError, gateIsUp, openTicket, presentAtGate, presentDemo, pollTicket, type DoorIdentity } from './door.ts';
/**
 * The client's catalogue, imported and not re-implemented: one lookup, one fallback chain, one place a
 * language is decided (`arena.md §C`). It is a leaf module with no scene and no engine behind it, which
 * is what makes it affordable on a page that has to open in a second over mobile data.
 */
import { applyCopy, t, tf } from '../client/i18n/index.ts';
import { Broker } from '../shared/coord/broker.ts';
import { Peer } from '../shared/coord/peer.ts';
import type { IcePair } from '../shared/coord/ice.ts';

const $ = (id: string): HTMLElement => document.getElementById(id) as HTMLElement;
const params = new URLSearchParams(location.search);

/**
 * WHERE THE APP IS GOT — a placeholder until there is a listing to point at.
 *
 * It lives here and not in the catalogue because it is an ADDRESS and not copy: translating it would
 * produce sixteen download pages. One definition, so the day the listing exists this line moves once.
 */
// PROD-CLEANUP: point at the real store listing before this door faces anybody outside the field.
const APP_URL = 'https://a-airsoft.app/app';

/**
 * THE STRING THAT OPENS THE DEMO, when the deployment names one (`server/http/gate.ts`).
 *
 * Read off the address because that is where somebody showing the game types it, and handed to the
 * service in a POST body: this page never decides whether it is the right one — the lock does.
 */
const demoAsked = (params.get('demo') ?? '').trim();

// Where the backend is, asked of the page rather than baked in: this client is served from several
// places and a constant would be a second description of a topology the ingress already holds.
// `?api=` overrides it, which is what makes the probe usable from a LAN address.
const API_BASE = params.get('api') ?? DEFAULT_API_BASE;
const api = new Api(API_BASE);

const log = (text: string): void => {
  const line = document.createElement('div');
  line.textContent = `${new Date().toLocaleTimeString()}  ${text}`;
  $('log').prepend(line);
};
const setState = (text: string): void => {
  $('state').textContent = text;
};
const put = (id: string, text: string): void => {
  $(id).textContent = text;
};

/**
 * THE APP'S WEBVIEW, IF THIS PAGE IS IN ONE — and it changes what a failure is allowed to draw.
 *
 * A phone that opened Arena from the app already IS the proof of who is here, so a code asking to be
 * scanned would be asking that phone to scan itself (`arena.md §D.8`). A desktop tab has no shell, no
 * session handed to it, and its code is the way in rather than a wrong screen.
 */
const shell = (): { postMessage(m: string): void } | undefined =>
  (window as unknown as { ReactNativeWebView?: { postMessage(m: string): void } }).ReactNativeWebView;

/**
 * WHERE THIS PAGE IS ASKED TO SEND THE PLAYER BACK TO.
 *
 * The game has no sign-in of its own and must not grow one (`shared/coord/bearer.ts`): it sends the tab
 * here and this page returns it. SAME-ORIGIN ONLY — a `next` pointing anywhere else would make this form
 * a redirector that carries a credential to whoever wrote the link.
 */
const next = ((): string => {
  const asked = params.get('next');
  if (!asked) return '';
  try {
    return new URL(asked, location.href).origin === location.origin ? asked : '';
  } catch {
    return '';
  }
})();

/**
 * WHO THIS BROWSER IS, once it is somebody.
 *
 * PAINTED AND NOT KEPT. This page holds no copy of the identity, because the game does not get it from
 * here: it asks the service, which reads it out of the pass it signed (`server/http/gate.ts`). A variable
 * here would be a second answer to "who is this player" living one navigation away from the first
 * (`arena.md §C`), and the two would differ the moment a bearer changed hands.
 */
function paintWho(identity: DoorIdentity): void {
  put('who-callsign', identity.callsign);
  put('who-team', identity.team ? `${identity.team.tag} · ${identity.team.name}` : '—');
  $('who').hidden = false;
  $('door').hidden = true;
}

/**
 * THE ONE WAY IN.
 *
 * This used to read an email and a password out of two inputs. It reads a phone now, and the inputs
 * are gone rather than hidden: a form kept beside a QR is the door, and the QR is the decoration.
 *
 * The loop is the human's, not the machine's — mint, paint, poll, claim — and every step that can fail
 * says which one failed, because the door is the surface where an unreadable failure means somebody
 * cannot play and does not know why.
 */
async function ensureBearer(): Promise<string> {
  const held = bearer();
  if (held) return held;
  /**
   * INSIDE THE APP THERE IS NOTHING TO SCAN, so this is where a code stops being minted.
   *
   * The phone that would have to read the QR is the one drawing it, and the session it should have
   * handed over is the app's business — so the way out is the app's own screen and not a second
   * credential this page can issue. A desktop tab has nobody to hand it one, and its code is right.
   */
  if (shell()) throw new DoorError('door.err_in_app', {}, 'no session reached the page inside the app shell');

  let ticket = await openTicket(API_BASE);
  // Its own deadline, computed once from what the backend said and never from a counter this page keeps:
  // one clock, which is the rule the ticket service already had to learn (`qr-login.md §5`).
  let lapsesAt = Date.now() + ticket.expires_in * 1000;
  const paintTicket = (): void => {
    $('qr').innerHTML = ticket.svg;
    put('pair-code', ticket.code);
    // Revealed HERE and nowhere else: the plate appears when it has a code, so there is no state in
    // which an empty one is on screen promising one (`index.html`).
    $('code').hidden = false;
  };
  paintTicket();
  log('waiting for the phone to approve');

  const deadline = Date.now() + 10 * 60_000;
  for (;;) {
    if (Date.now() > deadline) throw new DoorError('door.err_lapsed');
    put('pair-ttl', tf('door.ttl', { n: Math.max(0, Math.ceil((lapsesAt - Date.now()) / 1000)) }));
    await new Promise((r) => window.setTimeout(r, ticket.poll_interval_ms));

    const phase = await pollTicket(API_BASE, ticket);
    if (phase === 'approved') break;
    if (phase === 'denied') throw new DoorError('door.err_denied');
    // The service could not say. Rotating on that would mint a code against the same refusal; the
    // ceiling above is what still ends an abandoned page.
    if (phase === 'unknown') log('the door could not be asked about this code — waiting');
    if (phase === 'expired') {
      /*
       * ROTATED RATHER THAN FAILED. A code that lapses while somebody is walking to their phone is the
       * ordinary case, and an error there sends them back to a button. The ceiling above is what keeps
       * an abandoned page from emitting live credentials for ever (`qr-login.md §5`).
       */
      ticket = await openTicket(API_BASE);
      lapsesAt = Date.now() + ticket.expires_in * 1000;
      paintTicket();
      log('the code lapsed — here is a new one');
    }
  }

  const { token, identity } = await claimTicket(API_BASE, ticket);
  rememberBearer(token);
  paintWho(identity);
  log(`approved — ${identity.callsign}`);

  // And the SERVICE has to be told, or the bearer opens the plane and not the game (`door.ts`).
  const at = await presentAtGate(token);
  if (at.kind === 'admitted' && at.identity) paintWho(at.identity);
  // A pass the phone just approved and the service could not verify is not a bad pass: minting another
  // would burn the approval on a service that is going to answer the same thing.
  if (at.kind === 'later') throw at.err;
  if (at.kind === 'refused') log(`the service would not take the pass just claimed — ${at.why}`);

  return token;
}

let grant: RoomGrant | null = null;
let peer: Peer | null = null;
let broker: Broker | null = null;
/** Whom this page is talking to. One link at a time: T1 is about whether a pair connects, not about N. */
let partner: string | null = null;
/** Cleared on the way out — an interval that outlives its page is the shape of every leak in this repo. */
let pinger: number | null = null;
/** Candidates that arrived before there was a peer to give them to. Bounded like `PendingIce` is. */
const early: RTCIceCandidateInit[] = [];

// Report first, paint second: the POST is the deliverable and the paint is the courtesy. A browser
// closed after a good connection must not take the one number this tanda exists for with it.
async function report(pair: IcePair | null, connectMs: number, signalMs: number): Promise<void> {
  put('connect-ms', `${connectMs} ms`);
  put('signal-ms', `${signalMs} ms`);
  if (!pair) {
    // Said and not swallowed: a channel that opened without a readable pair is a measurement that did
    // not happen, which is a different outcome from one that came back "direct".
    log('the channel opened but no selected candidate pair could be read — nothing to report');
    return;
  }
  put('local-type', pair.localType);
  put('remote-type', pair.remoteType);
  put('protocol', pair.localProtocol ?? '—');
  put('relayed', pair.relayed ? 'YES — this one goes through TURN' : 'no — direct');
  $('relayed').className = pair.relayed ? 'bad' : 'good';

  if (!grant) return;
  try {
    const out = await api.iceReport(grant.room.code, grant.seat.token, {
      local_type: pair.localType,
      remote_type: pair.remoteType,
      local_protocol: pair.localProtocol,
      connect_ms: connectMs,
    });
    log(`reported: relayed=${out.relayed} turn_offered=${out.turn_offered}`);
    await api.connected(grant.room.code, grant.seat.token);
  } catch (err) {
    log(`could not report the measurement — ${String(err)}`);
  }
}

/**
 * The probe's own ping, which used to live inside `Peer`.
 *
 * It moved out when the game started using the same class: a round trip is what THIS page measures, and
 * an authority fanning snapshots has no use for a second latency figure beside the one `ClockSync`
 * already produces from `pong`.
 */
function pinged(ch: { send(p: string): void }, data: string | ArrayBuffer): void {
  if (typeof data !== 'string') return;
  const m = JSON.parse(data) as { t: string; at: number };
  if (m.t === 'ping') ch.send(JSON.stringify({ t: 'pong', at: m.at }));
  else if (m.t === 'pong') put('rtt', `${Math.round(performance.now() - m.at)} ms`);
}

function buildPeer(role: 'offerer' | 'answerer'): Promise<Peer> {
  const g = grant as RoomGrant;
  let self: Peer | null = null;
  const made = Peer.create({
    role,
    ice: iceProvider(api, bearer, g.iceServers),
    send: async (kind, payload) => {
      try {
        await api.signal(g.room.code, g.seat.token, partner, kind, payload as Record<string, unknown>);
      } catch (err) {
        // Reported, never swallowed: a candidate the backend refused is a negotiation that stalls,
        // and the point of the size guard is that the refusal has a name.
        log(`signal (${kind}) refused — ${String(err)}`);
      }
    },
    onNote: log,
    onState: (s) => put('ice-state', s),
    onUp: (pair, ms, signalMs) => {
      setState('CONNECTED');
      void report(pair, ms, signalMs);
      pinger = window.setInterval(() => self?.send(JSON.stringify({ t: 'ping', at: performance.now() })), 1000);
    },
    onData: (data) => pinged({ send: (p) => self?.send(p) }, data),
  });
  return made.then((p) => {
    self = p;
    return p;
  });
}

/** Wire the room channel. Nothing is signalled before this resolves — see `Broker`. */
async function joinChannel(): Promise<void> {
  const g = grant as RoomGrant;
  broker = new Broker({
    url: g.broker.url,
    key: g.broker.key,
    auth: (socketId) => api.channelAuth(g.room.code, g.seat.token, socketId),
    onNote: log,
    onEvent: (name, data) => {
      if (name !== 'signal') return;
      const m = data as { from: string; to: string | null; kind: string; payload: Record<string, unknown> };
      // Every seat hears the whole room and drops what is not for it; `to: null` is for everyone.
      if (m.from === g.seat.peer) return;
      if (m.to !== null && m.to !== g.seat.peer) return;
      // Reported, never swallowed: a `void` on a rejecting promise is how a negotiation that threw looks
      // exactly like one that never got a frame (`code-quality.md §12`).
      handle(m).catch((err) => log(`handling ${m.kind} from ${m.from} threw — ${String(err)}`));
    },
  });
  await broker.open();
  log(`on the room channel as ${g.seat.peer}`);
}

async function handle(m: { from: string; kind: string; payload: Record<string, unknown> }): Promise<void> {
  // Every frame is named. A negotiation that stalls is diagnosed from what ARRIVED, and `ice` is noisy
  // enough that leaving it out is how the interesting two get lost among them.
  if (m.kind !== 'ice') log(`<- ${m.kind} from ${m.from}`);
  if (!partner) {
    partner = m.from;
    put('partner', partner);
    log(`partner: ${partner}`);
  }
  // A frame from a third page is not this link's business — one pair at a time, on purpose.
  if (m.from !== partner) return;

  if (m.kind === 'offer') {
    if (!peer) {
      peer = await buildPeer('answerer');
      const held = early.splice(0);
      for (const c of held) void peer.onIce(c);
    }
    /**
     * THE STATE IS WRITTEN BEFORE THE AWAIT, NOT AFTER.
     *
     * Answering POSTs the answer, and on a fast path the channel opens while that request is still in
     * flight — so a line written afterwards lands on top of `CONNECTED` and the page reports a phase it
     * has already left. Caught by the smoke, which read a host that had reported its measurement while
     * still saying it was waiting.
     */
    setState('answering…');
    await peer.onOffer(m.payload as unknown as RTCSessionDescriptionInit);
    return;
  }
  if (m.kind === 'answer') {
    setState('applying the answer…');
    await peer?.onAnswer(m.payload as unknown as RTCSessionDescriptionInit);
    return;
  }
  if (m.kind === 'ice') {
    /**
     * HELD WHEN THERE IS NO PEER YET, and this is the layer that has to do it.
     *
     * `PendingIce` holds candidates that beat the remote DESCRIPTION; these beat the peer OBJECT. The
     * offer and the first candidates are separate POSTs with no ordering between them, so on the
     * answering side a candidate routinely arrives first — and a `peer?.` there drops it silently. Same
     * lesson as `rtc.ts`, one door further out.
     */
    if (!peer) {
      if (early.length < 64) early.push(m.payload as unknown as RTCIceCandidateInit);
      return;
    }
    await peer.onIce(m.payload as unknown as RTCIceCandidateInit);
    return;
  }
  if (m.kind === 'leave') {
    setState('the other side left');
    log('the other side left the room');
  }
}

function paintGrant(): void {
  const g = grant as RoomGrant;
  put('room-code', g.room.code);
  put('me', `${g.seat.peer} (${g.seat.role})`);
  put('turn', g.turnOffered ? 'yes — a relay is on offer' : 'NO — coturn is not deployed yet');
  $('turn').className = g.turnOffered ? 'good' : 'warn';
  put('ice-servers', g.iceServers.map((s) => (Array.isArray(s.urls) ? s.urls.join(' ') : String(s.urls))).join('\n'));
  const url = new URL(location.href);
  url.searchParams.set('room', g.room.code);
  put('guest-url', url.toString());
  $('room').hidden = false;
  $('gate').hidden = true;
}

async function host(): Promise<void> {
  setState('opening a room…');
  grant = await api.openRoom(await ensureBearer(), null);
  paintGrant();
  await joinChannel();
  setState('WAITING for the other side to open the link above');
}

async function guest(code: string): Promise<void> {
  setState('taking a seat…');
  grant = await api.joinRoom(await ensureBearer(), code);
  paintGrant();
  await joinChannel();
  setState('offering…');
  // The GUEST offers, because the host is already in the room: an offer into an empty room is an offer
  // nobody will ever answer, and the room's own state is what says whether there is anybody to answer it.
  peer = await buildPeer('offerer');
  partner = 'host';
  put('partner', partner);
  await peer.offer();
}

/**
 * WHAT THE PLAYER IS TOLD — one sentence per cause, and the backend's own code inside it.
 *
 * A door that collapses five refusals into one line sends somebody to fix the thing that was not broken
 * (`code-quality.md §12.2`). The log keeps the raw reason beside this, because the two have different
 * readers: this one is read by whoever cannot get in, that one by whoever has to find out why.
 */
function reason(err: unknown): string {
  if (err instanceof DoorError) return tf(err.key, err.vars);
  if (err instanceof ApiError) return tf('door.err_backend', { code: err.code });
  return t('door.err_unknown');
}

/** The countdown a not-now is running, so a second failure replaces it instead of stacking on it. */
let waiting: number | null = null;

function stopWaiting(): void {
  if (waiting !== null) window.clearInterval(waiting);
  waiting = null;
}

/**
 * A NOT-NOW COUNTS ITSELF DOWN AND GOES AGAIN.
 *
 * The service said for how long, so the page has everything it needs: leaving a person to press a
 * button at the right second is asking them to do the machine's counting, and pressing it early
 * reaches the same service and is told the same thing (`code-quality.md §12.2`).
 */
function recover(err: DoorError, waitS: number): void {
  stopWaiting();
  let left = waitS;
  const paint = (): void => {
    $('door-say').textContent = tf(err.key, { ...err.vars, s: left });
  };
  paint();
  waiting = window.setInterval(() => {
    left -= 1;
    paint();
    if (left <= 0) {
      stopWaiting();
      start();
    }
  }, 1000);
}

function fail(err: unknown): void {
  const why = err instanceof ApiError ? `${err.code} — ${err.message}` : String(err);
  setState(t('door.stopped'));
  log(why);
  /**
   * AND ON THE DOOR ITSELF, not only in the log.
   *
   * The log carries `probe-only`, so on a doorway it is HIDDEN — which meant a player whose backend
   * refused the origin saw an empty white plate and no sentence. That is the failure `arena.md §D.8`
   * calls worse than a hole: the screen looked like a QR that had not finished drawing. Caught by
   * `smoke-viewport`, running the path against a service whose origin the backend does not allow.
   */
  $('door-say').textContent = reason(err);
  $('door-say').classList.add('bad');
  $('gate').hidden = false;
  const waitS = err instanceof DoorError ? err.waitS : null;
  if (waitS !== null) {
    recover(err as DoorError, waitS);
    return;
  }
  // The retry is offered only where pressing it can reach a different answer. Inside the app there is
  // no second credential this page could ask for, so the way out is the shell's screen and not a button.
  ($('go') as HTMLButtonElement).disabled = !!shell();
}

/**
 * THE WAY IN, and there is ONE of it.
 *
 * Three things enter here — the automatic run, the button, and a not-now's own countdown — and a retry
 * has to repeat exactly what failed. Written twice, the button's copy would sign in and navigate
 * without presenting at the gate, so a refused session would bounce back here for ever (`arena.md §C`).
 */
function start(): void {
  stopWaiting();
  ($('go') as HTMLButtonElement).disabled = true;
  // Back to the INSTRUCTION and not to nothing: this line is the door's own copy when nothing has gone
  // wrong, and blanking it left the code on screen with no sentence saying what to do with it.
  $('door-say').textContent = t('door.scan');
  $('door-say').classList.remove('bad');
  $('code').hidden = true;
  const code = params.get('room');
  // With `next` this page is a DOOR and not a probe: sign in, and hand the tab back to whoever sent it.
  (next ? enter() : code ? guest(code) : host()).catch(fail);
}

$('go').addEventListener('click', start);

/**
 * SIGNING OUT IS TWO THINGS, and doing one of them is worse than doing neither.
 *
 * The bearer is this tab's and script can drop it; the pass is the service's cookie and only the service
 * can. Dropping the bearer alone left somebody holding the game and reading the word "signed out" —
 * a screen saying the opposite of what happened (`arena.md §D.8`).
 */
$('forget').addEventListener('click', () => {
  void (async () => {
    // ONE definition of what leaving means, shared with the game's own menu row: two would be two
    // sign-outs, and the day one of them stops dropping the pass nothing would say so (`arena.md §C`).
    await signOut();
    log('the bearer and the service pass were dropped');
    // Back to the door with nothing in hand, which is what the word promised. `reload` and not a paint:
    // whatever this page was showing described a session that no longer exists.
    location.reload();
  })();
});

/**
 * Leaving is announced: a seat that vanishes reads as a fault, one that says goodbye reads as a person.
 *
 * `fetch` with `keepalive` and NOT `sendBeacon`, which cannot carry a header — the room token rides in
 * one, so a beacon would arrive unauthenticated and be refused. A goodbye that 401s in silence is worse
 * than none: the roster then shows a seat nobody is in, and nothing says why.
 */
window.addEventListener('pagehide', () => {
  if (grant) {
    void fetch(`${API_BASE}/v1/arena/rooms/${grant.room.code}/leave`, {
      method: 'POST',
      keepalive: true,
      headers: { 'X-Arena-Room-Token': grant.seat.token, Accept: 'application/json' },
    }).catch(() => {
      // The page is going away; there is nobody left to report to.
    });
  }
  if (pinger) window.clearInterval(pinger);
  stopWaiting();
  peer?.close();
  broker?.close();
});

/**
 * THE ACKNOWLEDGEMENT FOR THE SHELL THAT OPENED THIS PAGE (`mobile/.../ArenaScreen.tsx`).
 *
 * The app writes a bearer into `sessionStorage` before this document loads, and only this page knows
 * whether it FOUND one. Losing that race draws a QR asking somebody to scan it with the phone already in
 * their hand — a wrong screen that reads as a working one, which is the failure `arena.md §D.8` calls
 * worse than a hole. Said out loud, the shell can name it instead.
 */
function tellShell(msg: Record<string, unknown>): void {
  const host = shell();
  if (!host) return;
  try {
    host.postMessage(JSON.stringify(msg));
  } catch (err) {
    // Reported and not swallowed, even here: a shell that is present and refuses the message is a
    // different fault from one that was never there, and only this line can tell them apart.
    log(`the shell would not take the door's report — ${String(err)}`);
  }
}

/**
 * WHAT THIS PAGE IS, decided by how the visitor got here.
 *
 * With `next` somebody was SENT here — by the game asking for a credential, or by the service's gate
 * refusing to hand over the field. That person came to get in, so the page is a DOOR and the probe's
 * instrument panels are hidden as a group: ICE candidate tables answer a question they did not ask.
 * Without `next` it is still T1's connectivity probe, and the door is simply how it signs in now.
 */
applyCopy();
put('mode', next ? t('door.mode') : params.get('room') ? `GUEST — joining ${params.get('room')}` : 'HOST — will open a room');
if (next) {
  document.body.classList.add('doorway');
  $('door-sub').hidden = false;
  $('go').textContent = t('door.proceed');
  ($('get-app') as HTMLAnchorElement).href = APP_URL;
  // A CONTROL THAT CAN DO NOTHING LOOKS LIKE IT. On the doorway the usual visitor is holding nothing, and
  // "sign out" beside "continue" offers to end a session that has not started. It appears for the tab that
  // arrived WITH one — the case where leaving is a real act, and the only one where it is the way out.
  ($('forget') as HTMLButtonElement).hidden = !bearer();
}

/**
 * THE WAY IN RUNS BY ITSELF, and the button is only for a retry.
 *
 * A player who was just refused the field and is then shown a Start button has been asked to confirm
 * that they meant to do what they were already doing. The QR appears because they arrived.
 */
async function enter(): Promise<void> {
  /**
   * THE DEMO IS ANSWERED FIRST, and it is the only branch that reaches nobody's phone.
   *
   * Before the held bearer on purpose: somebody typing the string is asking for THIS way in, and reusing
   * a pass left in the tab would put them back in whatever account opened it last. A refusal falls
   * through to the QR — the string being wrong says nothing about the door working.
   */
  if (demoAsked) {
    const at = await presentDemo(demoAsked);
    if (at.kind === 'admitted') {
      if (at.identity) paintWho(at.identity);
      location.replace(next);
      return;
    }
    if (at.kind === 'later') throw at.err;
    log(`the demo string was not accepted (${at.why}) — falling back to the phone`);
  }
  const held = bearer();
  tellShell({ t: 'arena.door', held: !!held });
  if (held) {
    /*
     * A bearer this tab already holds — the mobile viewport injects one before the page loads, and a
     * same-tab return keeps one alive. It still has to be PRESENTED: the plane accepts a bearer, the
     * service accepts a cookie, and only the service can mint that (`door.ts`).
     */
    log('a bearer from this tab is already held');
    const at = await presentAtGate(held);
    if (at.kind === 'admitted') {
      if (at.identity) paintWho(at.identity);
      location.replace(next);
      return;
    }
    /**
     * A SERVICE THAT COULD NOT DECIDE IS NOT A CREDENTIAL THAT WAS REFUSED.
     *
     * The bearer is KEPT and no code is minted: a fresh QR would reach the same service and be told the
     * same thing, and throwing away a live credential on the way is the part that cannot be undone.
     * Raised rather than painted here, so the wait it carries reaches the one place that counts it down.
     */
    if (at.kind === 'later') throw at.err;
    if (!(await gateIsUp())) {
      // No gate on this deployment: the held bearer is all there is to hold, and the game is open.
      location.replace(next);
      return;
    }
    /**
     * A HANDED-OVER SESSION THE SERVICE REFUSES IS NOT A MISSING ONE, and a QR is the wrong answer to it.
     *
     * The shell put this bearer here on purpose, so drawing a code asks somebody to scan it with the
     * phone that just supplied it — the symptom this report exists to replace with a sentence. Only the
     * shell knows it handed one over, so it is the one told; a desktop tab has nobody to tell and its QR
     * is the right move.
     */
    tellShell({ t: 'arena.gate', accepted: false, why: at.why });
    forgetBearer();
    log(`the held bearer was not accepted (${at.why}) — asking the phone again`);
  }
  await ensureBearer();
  location.replace(next);
}

// The button becomes a RETRY once the automatic attempt has failed — the one case where a player
// pressing something is the right next move rather than a confirmation of what they already did.
if (next) start();
